import React, { useState, useEffect } from 'react';
import { Mic, MicOff, Loader2, Check, X, AlertCircle } from 'lucide-react';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { voiceParserService, ParsedVoiceItem } from '@/services/voiceParserService';
import { productService } from '@/services/productService';
import { useAuth } from '@/hooks/useAuth';
import { VoiceModal } from './VoiceModal';
import { VoiceRecordingOverlay } from './VoiceRecordingOverlay';
import { hapticMedium, hapticStrong, hapticSuccess, hapticLight } from '@/utils/haptic';
import { useVoiceStore } from '@/stores/voiceStore';
import type { Product } from '@/types/product.types';

export const VoiceButton: React.FC = () => {
  const { currentRestaurantId, isAuthenticated } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [parsedItems, setParsedItems] = useState<ParsedVoiceItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [showUnsupportedMessage, setShowUnsupportedMessage] = useState(false);

  // Détecter si on est sur un appareil tactile (plus fiable que isMobile seul)
  const [isTouchDevice] = useState(() => {
    if (typeof window === 'undefined') return false;
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  });

  const {
    isListening,
    transcript,
    interimTranscript,
    error,
    isSupported,
    isMobile,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognition({
    language: 'fr-FR',
    continuous: true,
    interimResults: true,
  });

  // Utiliser l'overlay sur mobile ou appareil tactile
  const shouldUseOverlay = isMobile || isTouchDevice;

  // Écouter les demandes d'ouverture depuis la BottomNav
  const { isOverlayRequested, clearRequest } = useVoiceStore();

  useEffect(() => {
    if (isOverlayRequested) {
      clearRequest();
      if (!isOverlayOpen) {
        setIsOverlayOpen(true);
      }
    }
  }, [isOverlayRequested]);

  // Charger les produits au montage
  useEffect(() => {
    if (currentRestaurantId) {
      loadProducts();
    }
  }, [currentRestaurantId]);

  const loadProducts = async () => {
    if (!currentRestaurantId) return;

    const result = await productService.getProducts(currentRestaurantId);
    if (result.success && result.data) {
      setProducts(result.data);
    }
  };

  // Ne pas afficher si non authentifié
  if (!isAuthenticated) {
    return null;
  }

  // Si non supporté, afficher un bouton qui montre un message
  if (!isSupported) {
    return (
      <>
        <button
          onClick={() => setShowUnsupportedMessage(true)}
          className={`
            fixed z-50
            ${isMobile ? 'bottom-20 right-4 w-16 h-16' : 'bottom-6 right-6 w-14 h-14'}
            rounded-full
            flex items-center justify-center
            shadow-lg transition-all duration-200
            bg-gray-400
            text-white
            focus:outline-none
            active:scale-95
          `}
          title="Saisie vocale non disponible"
        >
          <Mic size={isMobile ? 28 : 24} />
        </button>

        {showUnsupportedMessage && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[100]"
              onClick={() => setShowUnsupportedMessage(false)}
            />
            <div className="fixed bottom-24 right-4 left-4 sm:left-auto sm:right-6 sm:w-80 z-[101] bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-yellow-100 rounded-full">
                  <AlertCircle size={20} className="text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Saisie vocale non disponible
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {isMobile
                      ? 'La reconnaissance vocale nécessite Safari 14.5+ sur iOS ou Chrome sur Android.'
                      : 'Votre navigateur ne supporte pas la reconnaissance vocale. Utilisez Chrome, Edge ou Safari.'}
                  </p>
                  <button
                    onClick={() => setShowUnsupportedMessage(false)}
                    className="w-full py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                  >
                    Compris
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  const handleClick = async () => {
    // Haptic feedback when tapping the voice button
    hapticMedium();

    if (shouldUseOverlay) {
      // Sur mobile/tactile, ouvrir l'overlay plein écran
      if (!isOverlayOpen) {
        setIsOverlayOpen(true);
      }
    } else {
      // Sur desktop, comportement existant
      if (isListening) {
        stopListening();
      } else {
        resetTranscript();
        setParsedItems([]);
        startListening();
      }
    }
  };

  const handleOverlayClose = () => {
    stopListening();
    resetTranscript();
    setParsedItems([]);
    setIsOverlayOpen(false);
  };

  const handleOverlayToggleListening = () => {
    // Strong haptic when toggling recording
    hapticStrong();

    if (isListening) {
      stopListening();
    } else {
      // Démarrer directement - l'API Speech Recognition gère les permissions
      resetTranscript();
      startListening();
    }
  };

  const handleValidate = () => {
    // Success haptic when validating
    hapticSuccess();
    stopListening();
    processTranscript();
  };

  const handleCancel = () => {
    // Light haptic when canceling
    hapticLight();
    stopListening();
    resetTranscript();
    setParsedItems([]);
    if (shouldUseOverlay) {
      setIsOverlayOpen(false);
    }
  };

  const processTranscript = () => {
    if (!transcript.trim()) {
      return;
    }

    setIsProcessing(true);

    // Parser le transcript
    const items = voiceParserService.parseTranscript(transcript, products);
    setParsedItems(items);

    setIsProcessing(false);

    if (items.length > 0) {
      if (shouldUseOverlay) {
        setIsOverlayOpen(false);
      }
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    resetTranscript();
    setParsedItems([]);
  };

  const handleStockAdded = () => {
    setIsModalOpen(false);
    resetTranscript();
    setParsedItems([]);
    // Recharger les produits pour avoir les dernières données
    loadProducts();
  };

  // Classes pour le bouton (plus grand sur mobile)
  const buttonClasses = `
    fixed z-50
    ${isMobile ? 'bottom-20 right-4 w-16 h-16' : 'bottom-6 right-6 w-14 h-14'}
    rounded-full
    flex items-center justify-center
    shadow-lg transition-all duration-200
    ${isListening
      ? 'bg-red-500 hover:bg-red-600 animate-pulse'
      : 'bg-primary hover:bg-primary-dark'
    }
    ${error ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
    text-white
    focus:outline-none focus:ring-4 focus:ring-primary/30
    active:scale-95
  `;

  return (
    <>
      {/* Interface mobile/tactile - Overlay plein écran */}
      {shouldUseOverlay && (
        <VoiceRecordingOverlay
          isOpen={isOverlayOpen}
          isListening={isListening}
          transcript={transcript}
          interimTranscript={interimTranscript}
          error={error}
          isProcessing={isProcessing}
          onClose={handleOverlayClose}
          onToggleListening={handleOverlayToggleListening}
          onValidate={handleValidate}
          onCancel={handleCancel}
        />
      )}

      {/* Interface desktop - Indicateur de transcription */}
      {!shouldUseOverlay && (isListening || interimTranscript || transcript) && (
        <div className="fixed bottom-24 right-6 z-50 max-w-sm">
          <div className="bg-white rounded-lg shadow-xl p-4 border-2 border-primary">
            <div className="flex items-center gap-2 mb-2">
              {isListening && (
                <>
                  <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-gray-700">
                    Parlez maintenant...
                  </span>
                </>
              )}
              {!isListening && transcript && (
                <span className="text-sm font-medium text-gray-700">
                  Transcription terminée
                </span>
              )}
            </div>
            {(transcript || interimTranscript) && (
              <p className="text-sm text-gray-600 mb-3">
                {transcript}
                {interimTranscript && (
                  <span className="text-gray-400"> {interimTranscript}</span>
                )}
              </p>
            )}
            {/* Boutons Valider / Annuler quand l'écoute est terminée */}
            {!isListening && transcript && (
              <div className="flex gap-2">
                <button
                  onClick={handleCancel}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X size={16} />
                  Annuler
                </button>
                <button
                  onClick={handleValidate}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors"
                >
                  <Check size={16} />
                  Valider
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bouton flottant (caché sur mobile car la BottomNav le remplace, caché quand overlay ouvert) */}
      {!shouldUseOverlay && !isOverlayOpen && (
        <button
          onClick={handleClick}
          className={buttonClasses}
          disabled={isProcessing}
          title={
            error
              ? error
              : isListening
                ? 'Cliquez pour arrêter'
                : isMobile
                  ? 'Appuyez pour dicter'
                  : 'Cliquez pour dicter'
          }
        >
          {isProcessing ? (
            <Loader2 size={isMobile ? 28 : 24} className="animate-spin" />
          ) : isListening && !isMobile ? (
            <MicOff size={24} />
          ) : (
            <Mic size={isMobile ? 28 : 24} />
          )}
        </button>
      )}

      {/* Message d'erreur desktop */}
      {!shouldUseOverlay && error && !isListening && (
        <div className="fixed bottom-24 right-6 z-50">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-w-xs">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Modal de prévisualisation */}
      <VoiceModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        transcript={transcript}
        parsedItems={parsedItems}
        products={products}
        onItemsUpdated={setParsedItems}
        onStockAdded={handleStockAdded}
        onProductCreated={(newProduct) => {
          setProducts((prev) => [...prev, newProduct]);
        }}
      />
    </>
  );
};
